import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { getAvatarUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePermission, Can } from '../lib/permissions';
import Portal from '../components/Portal';
import { Plus, X, Check, ChevronDown, Star } from 'lucide-react';

const COLORS = ['#0079BF', '#D29034', '#519839', '#B04632', '#89609E', '#CD5A91', '#4BBF6B', '#00AECC'];

export default function Projects() {
  const { user } = useAuth();
  const { has } = usePermission();
  const canCreate = has('projects.create');
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const p = await api.get('/projects'); setProjects(p.data.projects);
    if (has('users.view')) { const t = await api.get('/team'); setMembers(t.data.members); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="projects-page">
      <div className="flex items-center justify-between mb-6" style={{ color: 'var(--trello-text)' }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>Projects</h1>
          <p style={{ opacity: 0.85, marginTop: 4 }}>All boards you have access to</p>
        </div>
        <Can perm="projects.create">
          <button className="tm-btn" onClick={() => setShowNew(true)} data-testid="new-project-btn" style={{ padding: '8px 16px', borderRadius: 10 }}>
            <Plus size={16} style={{ display: 'inline', verticalAlign: -3, marginRight: 4 }} />New project
          </button>
        </Can>
      </div>

      {projects.filter(p => p.starred).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--trello-text)', marginBottom: 12 }}>
            <Star size={16} fill="#F2D600" stroke="#F2D600" />
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Starred boards</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.filter(p => p.starred).map(p => (
              <ProjectCard p={p} key={`starred-${p.id}`} testId={`project-card-starred-${p.id}`} />
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--trello-text)', marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>All boards</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map(p => (
          <ProjectCard p={p} key={p.id} testId={`project-card-${p.id}`} />
        ))}
        {projects.length === 0 && <div style={{ color: 'var(--trello-text)', gridColumn: '1/-1', textAlign: 'center', padding: 40 }}>No projects yet. {canCreate && 'Create your first one!'}</div>}
      </div>

      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} members={members} />}
    </div>
  );
}

function ProjectCard({ p, testId }) {
  const baseColor = p.color || '#0079BF';
  return (
    <Link to={`/projects/${p.id}`} key={p.id} data-testid={testId}
      className="group block rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[rgba(0,0,0,0.1)] border border-slate-200 dark:border-slate-700/50 relative bg-white dark:bg-slate-800"
      style={{ textDecoration: 'none', color: 'inherit' }}>
      
      {/* Top Banner */}
      <div 
        style={{ background: `linear-gradient(135deg, ${baseColor}cc 0%, ${baseColor} 100%)`, height: 90 }}
        className="px-5 py-4 flex flex-col justify-between relative overflow-hidden"
      >
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
        <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-black/10 rounded-full blur-xl" />
        
        <div className="flex justify-between items-start relative z-10">
          <div className="font-bold text-lg text-white drop-shadow-md truncate pr-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            {p.name}
          </div>
          {p.starred === 1 && (
            <div className="bg-white/20 p-1.5 rounded-full backdrop-blur-sm border border-white/30 text-yellow-300">
              <Star size={14} fill="currentColor" stroke="currentColor" />
            </div>
          )}
        </div>
      </div>
      
      {/* Bottom Content */}
      <div className="p-5 flex flex-col justify-between h-[110px]">
        <p className="m-0 text-sm text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed font-medium">
          {p.description || 'No description'}
        </p>
        
        {p.delegator_name && (
          <div className="flex items-center gap-2 mt-auto pt-4 border-t border-slate-100 dark:border-slate-700/50">
            <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 text-[10px] font-bold">
              {p.delegator_name.charAt(0).toUpperCase()}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              Manager: <span className="text-slate-700 dark:text-slate-300 font-bold">{p.delegator_name}</span>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

function NewProjectModal({ onClose, onSaved, members }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    color: COLORS[0],
    background_type: 'color',
    background_url: '',
    delegator_id: '',
    member_ids: []
  });
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [showOnlySelectedMembers, setShowOnlySelectedMembers] = useState(false);

  const toggleMember = (id) => setForm({ ...form, member_ids: form.member_ids.includes(id) ? form.member_ids.filter(x => x !== id) : [...form.member_ids, id] });

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({
        ...prev,
        color: '',
        background_type: 'image',
        background_url: data.url
      }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post('/projects', { ...form, delegator_id: form.delegator_id || null }); onSaved(); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const teamCandidates = members.filter((m) => m.role !== 'leader' && m.is_active && m.status !== 'deleted' && m.status !== 'pending');
  const displayedCandidates = showOnlySelectedMembers
    ? teamCandidates.filter(m => form.member_ids.includes(m.id))
    : teamCandidates;
  const managerCandidates = members.filter((m) => (m.role === 'manager' || m.role === 'leader') && m.is_active && m.status !== 'deleted' && m.status !== 'pending');
  const selectedCount = form.member_ids.filter(id => teamCandidates.some(tc => tc.id === id)).length;

  return (
    <Portal>
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal" onClick={(e) => e.stopPropagation()} data-testid="new-project-modal"
        style={{ maxWidth: 520, padding: 0, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Colored/Image banner header */}
        <div style={{
          padding: '20px 24px 24px',
          background: form.background_type === 'image' && form.background_url
            ? `url(${form.background_url}) center/cover`
            : `linear-gradient(135deg, ${form.color || '#0079BF'} 0%, ${(form.color || '#0079BF')}CC 100%)`,
          color: '#fff', position: 'relative',
        }}>
          <button onClick={onClose} data-testid="close-new-project"
            style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 999, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} />
          </button>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.2, opacity: 0.8, fontWeight: 700 }}>Create board</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: '4px 0 0', letterSpacing: -0.3 }}>
            {form.name || 'New project'}
          </h2>
          {form.description && (
            <div style={{ fontSize: 13, marginTop: 4, opacity: 0.9 }}>{form.description}</div>
          )}
        </div>

        <form onSubmit={submit} style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {/* Name */}
          <div style={{ marginBottom: 16 }}>
            <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>PROJECT NAME</label>
            <input data-testid="project-name" className="tm-input"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Marketing website 2026" required autoFocus
              style={{ fontSize: 15, padding: '10px 12px' }} />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 18 }}>
            <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>DESCRIPTION</label>
            <textarea data-testid="project-desc" className="tm-input" rows={2}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What is this project about? (optional)"
              style={{ fontSize: 14, padding: '10px 12px', resize: 'vertical' }} />
          </div>

          {/* Team members */}
          <div style={{ marginBottom: 16 }}>
            <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>TEAM MEMBERS</span>
              {selectedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowOnlySelectedMembers(prev => !prev)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: form.color || '#0079BF',
                    fontWeight: 700,
                    cursor: 'pointer',
                    padding: 0,
                    textTransform: 'uppercase',
                    fontSize: 11
                  }}
                  data-testid="toggle-selected-members-filter"
                >
                  {showOnlySelectedMembers ? 'Show all' : `${selectedCount} selected`}
                </button>
              )}
            </label>
            {displayedCandidates.length === 0 ? (
              <div style={{ padding: 12, background: 'var(--trello-list-bg)', borderRadius: 6, fontSize: 13, color: 'var(--trello-text-muted)', textAlign: 'center' }}>
                {showOnlySelectedMembers ? 'No selected members.' : 'No teammates yet. Invite them from the Users page.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} data-testid="member-chips">
                {displayedCandidates.map((m) => {
                  const on = form.member_ids.includes(m.id);
                  const initials = (m.full_name || m.email).split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();
                  return (
                    <button key={m.id} type="button" onClick={() => toggleMember(m.id)} data-testid={`member-${m.id}`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 6px',
                        borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        background: on ? (form.color || '#0079BF') : '#fff',
                        color: on ? '#fff' : '#000000',
                        border: on ? `2px solid ${form.color || '#0079BF'}` : '2px solid #DFE1E6',
                        transition: 'all 150ms',
                      }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: m.avatar_url ? `url(${getAvatarUrl(m.avatar_url)}) center/cover` : (m.avatar_color || 'var(--trello-text-muted)'),
                        color: '#fff', fontSize: 10, fontWeight: 700,
                      }}>{!m.avatar_url && initials}</span>
                      {m.full_name.split(' ')[0]}
                      {on && <Check size={12} strokeWidth={3} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Advanced options */}
          <button type="button" onClick={() => setShowAdvanced((v) => !v)} data-testid="toggle-advanced"
            style={{ background: 'none', border: 'none', color: 'var(--trello-text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ChevronDown size={12} style={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
            {showAdvanced ? 'Hide' : 'Show'} advanced options
          </button>
          {showAdvanced && (
            <div style={{ marginTop: 12, padding: 12, background: 'var(--trello-list-bg)', borderRadius: 6 }}>
              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>BOARD COLOR</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {COLORS.map((c) => (
                  <button type="button" key={c} onClick={() => setForm({ ...form, color: c, background_type: 'color', background_url: '' })} data-testid={`color-${c}`}
                    style={{
                      width: 34, height: 34, borderRadius: 8, background: c, cursor: 'pointer',
                      border: form.color === c ? '3px solid var(--trello-text)' : '2px solid #fff',
                      boxShadow: form.color === c ? `0 0 0 2px ${c}66` : 'none',
                      transition: 'all 120ms',
                    }} />
                ))}
              </div>

              {/* Upload Banner */}
              <div style={{ marginTop: 14 }}>
                <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)' }}>UPLOAD BANNER IMAGE</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                  <input
                    type="file"
                    accept="image/*"
                    id="new-banner-upload"
                    onChange={handleBannerUpload}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="new-banner-upload"
                    className="tm-btn"
                    style={{
                      background: form.color || '#0079BF',
                      color: '#fff',
                      cursor: 'pointer',
                      padding: '6px 12px',
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: 4,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    {uploadingBanner ? 'Uploading...' : 'Choose image'}
                  </label>
                  {form.background_type === 'image' && form.background_url && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img
                        src={form.background_url}
                        alt="Banner preview"
                        style={{ width: 48, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #DFE1E6' }}
                      />
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, background_type: 'color', background_url: '', color: COLORS[0] }))}
                        style={{ background: 'none', border: 'none', color: '#DE350B', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <label className="tm-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--trello-text-muted)', marginTop: 12 }}>MANAGER (OPTIONAL)</label>
              <select className="tm-input" value={form.delegator_id} onChange={(e) => {
                const managerId = e.target.value;
                const newMemberIds = [...form.member_ids];
                if (managerId && !newMemberIds.includes(managerId)) {
                  newMemberIds.push(managerId);
                }
                setForm({ ...form, delegator_id: managerId, member_ids: newMemberIds });
              }} data-testid="project-delegator">
                <option value="">— No manager assigned —</option>
                {managerCandidates.map((m) => (
                  <option key={m.id} value={m.id}>{m.full_name} · {m.role}</option>
                ))}
              </select>
            </div>
          )}
        </form>

        {/* Sticky action bar */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--trello-border)', background: 'var(--trello-card-bg)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="tm-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" onClick={submit} className="tm-btn" disabled={busy || !form.name.trim()} data-testid="create-project-submit"
            style={{ background: form.color || '#0079BF', padding: '10px 20px', fontWeight: 700, letterSpacing: 0.2 }}>
            {busy ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
